/*
 * Connected Applications — Identity sub-surface.
 *
 * Operator management of the applications allowed to connect to the governed MCP surface. An admin
 * can register an application, review the registered applications, and revoke or restore one.
 *
 * An application is registered as one of two types:
 *   • Public — signs in with PKCE and no secret. The client ID is safe to show and copy. Most
 *     self-registering clients use this.
 *   • Confidential — signs in with a client secret, for applications whose connector expects a
 *     client id + secret. The secret is shown once, right after registration, and never again.
 *
 * Backend: /api/v1/oauth-clients (gateway.key.read / .create / .revoke / .reactivate); the
 * authorization + token URLs come from the authorization-server metadata so they always match the
 * surface's public address.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Copy, Check, Plug } from 'lucide-react';

import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { Table } from '../components/v2/Table';
import { EmptyState } from '../components/v2/EmptyState';
import { FieldGroup, Input, Textarea } from '../components/v2/Input';
import MyAuthorizedAppsPanel from '../components/v2/MyAuthorizedAppsPanel';
import { useToast } from '../contexts/ToastContext';
import {
  listOAuthClients,
  registerOAuthClient,
  updateOAuthClient,
  revokeOAuthClient,
  reactivateOAuthClient,
} from '../services/api';

/* Split a textarea of redirect URIs (one per line, or comma-separated) into a clean list. */
function parseRedirectUris(raw) {
  return (raw || '')
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/* Small inline copy control — shows a check for a moment after copying. */
function CopyValue({ value, ariaLabel }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the value is still visible for manual copy */
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <code className="text-[12px] text-v2-ink-secondary break-all">{value}</code>
      <button
        type="button"
        onClick={copy}
        aria-label={ariaLabel || 'Copy'}
        className="text-v2-ink-secondary hover:text-v2-ink shrink-0"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </span>
  );
}

export default function IdentityConnectedAppsPageV2() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [uris, setUris] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null); // row awaiting revoke confirmation
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);       // client_id being edited, or null for create
  const [clientType, setClientType] = useState('public'); // 'public' (PKCE) | 'confidential' (secret)
  const [createdApp, setCreatedApp] = useState(null);     // one-time confidential setup panel
  const [discovery, setDiscovery] = useState(null);       // authorization-server metadata URLs

  // Recommended scope for a connector that needs an explicit value; refresh tokens are always issued.
  const RECOMMENDED_SCOPE = 'mcp offline_access';

  // Load the authorization-server metadata so the confidential setup panel shows the exact
  // authorization + token URLs for this surface's public address (tunnel/ingress aware).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/.well-known/oauth-authorization-server', {
          headers: { Accept: 'application/json' }, credentials: 'include',
        });
        if (!r.ok) return;
        const meta = await r.json();
        if (!cancelled) setDiscovery(meta);
      } catch {
        /* discovery is best-effort; the panel falls back to guidance text */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setUris('');
    setClientType('public');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.client_id);
    setName(row.client_name || '');
    setUris((row.redirect_uris || []).join('\n'));
    setClientType(row.client_type === 'confidential' ? 'confidential' : 'public');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName('');
    setUris('');
    setClientType('public');
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await listOAuthClients();
      if (!res?.ok) {
        addToast({ tone: 'danger', title: 'Could not load applications', description: res?.error });
        setApps([]);
        return;
      }
      const list = res.data?.applications;
      setApps(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitForm = async () => {
    const redirect_uris = parseRedirectUris(uris);
    if (!name.trim()) {
      addToast({ tone: 'danger', title: 'Name required', description: 'Give the application a name.' });
      return;
    }
    if (redirect_uris.length === 0) {
      addToast({ tone: 'danger', title: 'Redirect URL required',
        description: 'Add the callback URL the application returns to after sign-in.' });
      return;
    }
    const payload = { client_name: name.trim(), redirect_uris };
    // Client type is chosen at registration and is fixed thereafter, so it is only sent on create.
    if (!editingId && clientType === 'confidential') {
      payload.token_endpoint_auth_method = 'client_secret_post';
    }
    setSubmitting(true);
    try {
      const res = editingId
        ? await updateOAuthClient(editingId, payload)
        : await registerOAuthClient(payload);
      if (!res?.ok) {
        addToast({ tone: 'danger',
          title: editingId ? 'Could not save changes' : 'Could not register application',
          description: res?.error });
        return;
      }
      // A confidential registration returns a secret exactly once — show the setup panel with it
      // instead of a transient toast, so the operator can copy everything the connector needs.
      if (!editingId && res.data?.client_secret) {
        setCreatedApp(res.data);
        closeForm();
        await load();
        return;
      }
      addToast(editingId
        ? { tone: 'success', title: 'Changes saved',
            description: 'The application keeps the same client ID.' }
        : { tone: 'success', title: 'Application registered',
            description: `Copy its client ID into ${name.trim()} to finish connecting.` });
      closeForm();
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  const doRevoke = async (id) => {
    setBusyId(id);
    try {
      const res = await revokeOAuthClient(id);
      if (!res?.ok) {
        addToast({ tone: 'danger', title: 'Could not revoke application', description: res?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Application revoked',
        description: 'It can no longer start new sign-ins, and its active sessions were ended.' });
      await load();
    } finally {
      setBusyId(null);
      setConfirmingId(null);
    }
  };

  const doReactivate = async (id) => {
    setBusyId(id);
    try {
      const res = await reactivateOAuthClient(id);
      if (!res?.ok) {
        addToast({ tone: 'danger', title: 'Could not restore application', description: res?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Application restored' });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'client_name',
      header: 'Application',
      render: (r) => <span className="font-medium text-v2-ink">{r.client_name}</span>,
    },
    {
      key: 'client_id',
      header: 'Client ID',
      render: (r) => <CopyValue value={r.client_id} ariaLabel="Copy client ID" />,
    },
    {
      key: 'redirect_uris',
      header: 'Redirect URLs',
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          {(r.redirect_uris || []).map((u) => (
            <code key={u} className="text-[12px] text-v2-ink-secondary break-all">{u}</code>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.status === 'active'
          ? <Pill tone="success">Active</Pill>
          : <Pill tone="neutral">Revoked</Pill>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => {
        if (confirmingId === r.client_id) {
          return (
            <span className="inline-flex items-center gap-2 justify-end">
              <span className="text-[12px] text-v2-ink-secondary">Revoke access?</span>
              <Button variant="danger" onClick={() => doRevoke(r.client_id)}
                loading={busyId === r.client_id}>
                Revoke
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingId(null)}>Cancel</Button>
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-2 justify-end">
            <Button variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
            {r.status === 'active'
              ? <Button variant="ghost" onClick={() => setConfirmingId(r.client_id)}>Revoke</Button>
              : <Button variant="secondary" onClick={() => doReactivate(r.client_id)}
                  loading={busyId === r.client_id}>Restore</Button>}
          </span>
        );
      },
    },
  ];

  return (
    <V2PageShell
      title="Connected Applications"
      subtitle="Applications allowed to sign your people in to the protected surface."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="ghost" iconLeft={<ArrowLeft size={16} />} onClick={() => navigate('/identity')}>
            Back to Identity
          </Button>
          {!showForm && (
            <Button variant="primary" iconLeft={<Plus size={16} />} onClick={openCreate}>
              Register application
            </Button>
          )}
        </div>
      }
    >
      {showForm && (
        <Card className="mb-4">
          <div className="flex flex-col gap-4 p-1">
            <div>
              <h3 className="text-[15px] font-semibold text-v2-ink m-0">
                {editingId ? 'Edit application' : 'Register an application'}
              </h3>
              <p className="text-[13px] text-v2-ink-secondary mt-1 mb-0">
                {editingId
                  ? "Update the name or callback URLs. The client ID stays the same, so the connected application keeps working."
                  : "Enter the application's name and the callback URL it returns to after a person signs in. You'll get a client ID to paste into that application's connector settings."}
              </p>
            </div>
            {!editingId && (
              <FieldGroup label="How does this application sign in?">
                <div className="flex flex-col gap-2 sm:flex-row">
                  {[
                    { id: 'public', title: 'With PKCE (public)',
                      desc: 'No secret. Best for apps that register themselves.' },
                    { id: 'confidential', title: 'With a client secret (confidential)',
                      desc: "For a connector that asks for a client ID and secret." },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setClientType(opt.id)}
                      className={
                        'flex-1 text-left rounded-lg border p-3 transition-colors ' +
                        (clientType === opt.id
                          ? 'border-v2-accent bg-v2-accent-soft'
                          : 'border-v2-border hover:border-v2-ink-secondary')
                      }
                      aria-pressed={clientType === opt.id}
                    >
                      <div className="text-[13px] font-medium text-v2-ink">{opt.title}</div>
                      <div className="text-[12px] text-v2-ink-secondary mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </FieldGroup>
            )}
            <FieldGroup label="Application name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude Cowork" />
            </FieldGroup>
            <FieldGroup label="Redirect URLs" required
              hint="One per line. The callback URL from the application's connector documentation. For a desktop app that signs in on a loopback address, omit the port (e.g. http://127.0.0.1/callback).">
              <Textarea rows={3} value={uris} onChange={(e) => setUris(e.target.value)}
                placeholder={'https://example.com/callback'} mono />
            </FieldGroup>
            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={submitForm} loading={submitting}>
                {editingId ? 'Save changes' : 'Register'}
              </Button>
              <Button variant="ghost" onClick={closeForm}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      {createdApp && createdApp.client_secret && (
        <Card className="mb-4 border-v2-accent">
          <div className="flex flex-col gap-4 p-1">
            <div>
              <h3 className="text-[15px] font-semibold text-v2-ink m-0">
                {createdApp.client_name} — connector setup
              </h3>
              <p className="text-[13px] text-v2-ink-secondary mt-1 mb-0">
                Copy these into the application's connector settings. The client secret is shown
                <span className="font-semibold"> once</span> — if you leave this page you'll need to
                register the application again to get a new one.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { label: 'Client ID', value: createdApp.client_id },
                { label: 'Client secret', value: createdApp.client_secret },
                { label: 'Authorization URL', value: discovery?.authorization_endpoint },
                { label: 'Token URL', value: discovery?.token_endpoint },
                { label: 'Scope', value: RECOMMENDED_SCOPE },
              ].map((f) => (
                <div key={f.label} className="flex flex-col gap-1">
                  <span className="text-[12px] font-medium text-v2-ink-secondary uppercase tracking-wide">
                    {f.label}
                  </span>
                  {f.value
                    ? <CopyValue value={f.value} ariaLabel={`Copy ${f.label}`} />
                    : <span className="text-[12px] text-v2-ink-secondary">
                        Not available — open the authorization-server metadata to read this URL.
                      </span>}
                </div>
              ))}
            </div>
            <p className="text-[12px] text-v2-ink-secondary m-0">
              The application must return to one of the redirect URLs you registered. Confirm its
              connector uses the same callback URL.
            </p>
            <div>
              <Button variant="primary" onClick={() => setCreatedApp(null)}>Done</Button>
            </div>
          </div>
        </Card>
      )}

      <Card padding={loading || apps.length === 0 ? 'md' : 'none'}>
        {loading ? (
          <p className="text-[13px] text-v2-ink-secondary p-2 m-0">Loading applications…</p>
        ) : apps.length === 0 ? (
          <EmptyState
            icon={<Plug size={20} />}
            title="No applications yet"
            description="Register an application to give it a client ID for connecting to the protected surface."
            primaryAction={
              <Button variant="primary" iconLeft={<Plus size={16} />} onClick={openCreate}>
                Register application
              </Button>
            }
          />
        ) : (
          <Table columns={columns} rows={apps} rowKey={(r) => r.client_id} ariaLabel="Connected applications" />
        )}
      </Card>

      <div className="mt-6 pt-5 border-t border-v2-border">
        <MyAuthorizedAppsPanel />
      </div>
    </V2PageShell>
  );
}
